import { sortPlantafelEmployees } from '../../utils/plantafelEmployeeOrder';
import { memberName } from './taskViews';
import type { Task, TaskChecklistItem, TaskLeadOption, TaskMember, TaskPatch } from './types';

async function readJson<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(body.error || 'Anfrage fehlgeschlagen.');
  return body;
}

function send(url: string, method: string, payload?: unknown): Promise<Response> {
  return fetch(url, {
    method,
    credentials: 'include',
    headers: payload === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
}

const taskUrl = (id: string) => `/api/tasks/${encodeURIComponent(id)}`;

/** Offene To-dos + erledigte der letzten 90 Tage. */
export async function fetchTasks(): Promise<Task[]> {
  const res = await fetch('/api/tasks', { credentials: 'include' });
  return (await readJson<{ items: Task[] }>(res)).items;
}

/** Ältere erledigte To-dos seitenweise (neueste zuerst). */
export async function fetchOlderDoneTasks(before: string, limit = 100): Promise<{ items: Task[]; hasMore: boolean }> {
  const qs = new URLSearchParams({ done_before: before, limit: String(limit) });
  const res = await fetch(`/api/tasks?${qs}`, { credentials: 'include' });
  const data = await readJson<{ items: Task[]; has_more?: boolean }>(res);
  return { items: data.items, hasMore: Boolean(data.has_more) };
}

/** Alle To-dos einer Anfrage (Projekt-Verlauf). */
export async function fetchLeadTasks(leadId: string): Promise<Task[]> {
  const res = await fetch(`/api/tasks?lead_id=${encodeURIComponent(leadId)}`, { credentials: 'include' });
  return (await readJson<{ items: Task[] }>(res)).items;
}

export async function fetchTaskLeadOptions(): Promise<TaskLeadOption[]> {
  const res = await fetch('/api/tasks/lead-options', { credentials: 'include' });
  return (await readJson<{ items: TaskLeadOption[] }>(res)).items;
}

/** Aktive Mitglieder (ohne offene Einladungen), sortiert wie die Mitarbeiter-Übersicht: Inhaber, Admins, Rest — je alphabetisch. */
export async function fetchTaskMembers(): Promise<TaskMember[]> {
  const res = await fetch('/api/me/members', { credentials: 'include' });
  const data = await readJson<{
    members?: {
      user_id?: string;
      email?: string;
      display_name?: string | null;
      invited?: boolean;
      platform_access?: string | null;
      role?: string | null;
    }[];
  }>(res);
  const members: TaskMember[] = (data.members ?? [])
    .filter((m) => m.user_id && !m.invited)
    .map((m) => ({
      id: String(m.user_id),
      email: String(m.email ?? ''),
      display_name: m.display_name ?? null,
      web_access: m.platform_access !== 'field_only',
      role: m.role === 'owner' || m.role === 'admin' || m.role === 'member' ? m.role : null,
    }));
  return sortPlantafelEmployees(members.map((m) => ({ ...m, name: memberName(m), membership_role: m.role })))
    .map(({ name: _name, membership_role: _role, ...m }) => m);
}

export async function createTask(payload: TaskPatch & { title: string }): Promise<Task> {
  return (await readJson<{ item: Task }>(await send('/api/tasks', 'POST', payload))).item;
}

export async function updateTask(id: string, patch: TaskPatch): Promise<Task> {
  return (await readJson<{ item: Task }>(await send(taskUrl(id), 'PATCH', patch))).item;
}

export async function deleteTask(id: string): Promise<void> {
  await readJson(await send(taskUrl(id), 'DELETE'));
}

export async function restoreTask(id: string): Promise<Task> {
  return (await readJson<{ item: Task }>(await send(`${taskUrl(id)}/restore`, 'POST'))).item;
}

export async function addChecklistItem(taskId: string, text: string): Promise<TaskChecklistItem> {
  return (await readJson<{ item: TaskChecklistItem }>(await send(`${taskUrl(taskId)}/checklist`, 'POST', { text }))).item;
}

export async function updateChecklistItem(
  taskId: string,
  itemId: string,
  patch: Partial<Pick<TaskChecklistItem, 'text' | 'done'>>,
): Promise<TaskChecklistItem> {
  const res = await send(`${taskUrl(taskId)}/checklist/${encodeURIComponent(itemId)}`, 'PATCH', patch);
  return (await readJson<{ item: TaskChecklistItem }>(res)).item;
}

export async function reorderChecklist(taskId: string, ids: string[]): Promise<void> {
  await readJson(await send(`${taskUrl(taskId)}/checklist/order`, 'PUT', { ids }));
}

export async function deleteChecklistItem(taskId: string, itemId: string): Promise<void> {
  await readJson(await send(`${taskUrl(taskId)}/checklist/${encodeURIComponent(itemId)}`, 'DELETE'));
}
